import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const RealEstateLeadMagnet = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Load OptinMonster script
    const script = document.createElement('script');
    script.src = 'https://a.optinmonster.com/app/js/api.min.js';
    script.async = true;
    script.setAttribute('data-account', '12');
    script.setAttribute('data-user', '12');
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>5 Free & Low Cost Real Estate Lead Generation Ideas - Sales Intercept</title>
        <meta name="description" content="Download our free guide with 5 creative, easy-to-implement lead generation tactics for real estate agents. Get immediate results with these cost-effective marketing ideas." />
        <meta name="description" content="Unlock 5 free & low-cost real estate lead generation ideas. Download our guide for creative, easy-to-implement tactics to get more leads without breaking the bank." />
        <meta name="keywords" content="real estate lead generation, real estate marketing, lead generation ideas, real estate agents, free download" />
        <meta property="og:title" content="5 Free & Low Cost Real Estate Lead Generation Ideas" />
        <meta property="og:description" content="Download our free guide with 5 creative, easy-to-implement lead generation tactics for real estate agents." />
        <meta property="og:image" content="https://media.salesintercept.com/og-image.jpg" />
        <meta property="og:url" content="https://salesintercept.com/free-download/real-estate-lead-ideas" />
        <link rel="canonical" href="https://salesintercept.com/free-download/real-estate-lead-ideas" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-purple-si/10 to-aqua-si/10">
        {/* Header */}
        <header className="py-6 bg-white shadow-sm">
          <div className="container mx-auto px-4">
            <div className="flex justify-center">
              <img 
                src="https://media.salesintercept.com/Sales_Intercept_rec_Logo.png" 
                alt="Sales Intercept Logo" 
                className="h-12"
              />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <section className="py-16 pt-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {/* Content Section - First Row */}
              <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <div className="mb-8">
                  <span className="inline-block bg-orange-dark-si text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                    FREE DOWNLOAD
                  </span>
                  <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                    <span className="text-purple-si">5 Free & Low Cost</span><br />
                    Lead Generation Ideas
                  </h1>
                  <p className="text-xl text-gray-700 mb-8">
                    For Real Estate Agents Who Want More Leads Without Breaking the Bank
                  </p>
                </div>

                {/* Benefits */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                      </svg>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">Creative, Easy-to-Implement Tactics</h3>
                    <p className="text-gray-600 text-center">Get immediate results with proven strategies that work</p>
                  </div>

                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                      </svg>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">Free & Cost-Effective Marketing Ideas</h3>
                    <p className="text-gray-600 text-center">Attract buyers and sellers without spending a fortune</p>
                  </div>

                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                      </svg>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">Instant Download</h3>
                    <p className="text-gray-600 text-center">Get access immediately after signing up</p>
                  </div>
                </div>

                {/* Social Proof */}
                <div className="bg-white rounded-lg p-6 shadow-md mb-8 max-w-lg mx-auto">
                  <div className="flex items-center justify-center mb-3">
                    <div className="flex -space-x-2">
                      <img className="w-8 h-8 rounded-full border-2 border-white" src="https://images.pexels.com/photos/3184328/pexels-photo-3184328.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2" alt="Agent 1" />
                      <img className="w-8 h-8 rounded-full border-2 border-white" src="https://images.pexels.com/photos/3184611/pexels-photo-3184611.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2" alt="Agent 2" />
                      <img className="w-8 h-8 rounded-full border-2 border-white" src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2" alt="Agent 3" />
                    </div>
                    <span className="ml-3 text-sm font-medium text-gray-700">Join 2,847+ agents</span>
                  </div>
                  <p className="text-sm text-gray-600 italic text-center">
                    "These strategies helped me generate 23 new leads in my first month!" - Sarah K., Real Estate Agent
                  </p>
                </div>
              </motion.div>

              {/* Form Section - Second Row */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex justify-center"
              >
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100 w-full max-w-lg">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Get Your Free Guide
                    </h2>
                    <p className="text-gray-600">
                      Enter your details below to download instantly
                    </p>
                  </div>

                  {/* OptinMonster Form */}
                  <div className="om-embedded-campaign" data-campaign-id="12"></div>

                  {/* Trust Indicators */}
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                        </svg>
                        Secure
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                        </svg>
                        No Spam
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Instant Access
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* What's Inside Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-12">
                What's Inside Your Free Guide
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-purple-si/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📱</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Social Media Strategies</h3>
                  <p className="text-gray-600">Leverage platforms you're already using to generate quality leads</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-orange-dark-si/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🏠</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Neighborhood Marketing</h3>
                  <p className="text-gray-600">Become the go-to agent in your area with these local tactics</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-aqua-si/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">💡</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Creative Content Ideas</h3>
                  <p className="text-gray-600">Stand out from the competition with unique content approaches</p>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-8">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <img 
                src="https://media.salesintercept.com/Sales-Intercept-rec-Logo-white-text.png" 
                alt="Sales Intercept Logo" 
                className="h-8 mx-auto mb-4"
              />
              <p className="text-gray-400 text-sm">
                © 2025 Sales Intercept, Inc. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default RealEstateLeadMagnet;